import type { Edge, Node } from '@xyflow/react';

export interface BlastRadiusResult {
  /** Set of node IDs that break (or degrade) if the focused node fails. Always
   * includes the focused node itself. Empty if the focused ID isn't in nodes. */
  affected: Set<string>;
  /**
   * Set of node IDs whose individual failure would take down >25% of the
   * total nodes. Computed in the same pass for cheapness — the UI can use
   * this to surface a permanent "⚠ SPOF" badge without re-traversing.
   *
   * Note: `spofs` is global to the diagram, not specific to the current
   * focus. Same value comes out regardless of which node is sourceId.
   */
  spofs: Set<string>;
}

/**
 * computeBlastRadius answers "what breaks if this node fails?" by walking
 * the dependency graph backwards from the source node.
 *
 * Edge convention in Clyra diagrams: an edge goes from the dependent to
 * its dependency (subnet → vpc; api → db; reverse-ingestion preserves
 * this through the regex). So a node's *incoming* edges point at the
 * things that depend on it — the things that break when it dies. We BFS
 * along incoming edges from the source.
 *
 * Bonus pass while we have the graph in memory: detect SPOFs (nodes
 * whose individual failure cascades to >25% of all nodes). The threshold
 * matches the gut-feel definition of "critical path" — any single node
 * taking out a quarter of the system is worth a permanent badge.
 *
 * O(V + E) per call. We don't memoize because diagrams are small
 * (sub-100 nodes typical) and React rebuilds the result on focus change
 * regardless.
 */
export function computeBlastRadius(
  nodes: Node[],
  edges: Edge[],
  sourceId: string,
): BlastRadiusResult {
  const result: BlastRadiusResult = {
    affected: new Set<string>(),
    spofs: new Set<string>(),
  };

  // Build an incoming-edge adjacency map once. For node N, incoming[N]
  // lists every node that has an edge pointing AT N — i.e. every node
  // that depends on N. That's what we follow during the failure BFS.
  const incoming = new Map<string, string[]>();
  for (const edge of edges) {
    const list = incoming.get(edge.target);
    if (list) {
      list.push(edge.source);
    } else {
      incoming.set(edge.target, [edge.source]);
    }
  }

  // Sanity check: if the source isn't a real node ID, bail with empty.
  const knownIDs = new Set(nodes.map((n) => n.id));
  if (knownIDs.has(sourceId)) {
    result.affected = bfsDependents(sourceId, incoming);
  }

  // SPOF pass — for every node, see how many things its failure would
  // take down. Threshold (>25% of total) holds at every diagram size:
  // even on a 3-node graph, a node that kills both others is a real SPOF.
  const threshold = nodes.length * 0.25;
  for (const node of nodes) {
    const reach = bfsDependents(node.id, incoming);
    // Subtract the focused node itself from the count so a leaf node
    // (which only ever 'affects' itself) doesn't trivially count toward
    // its own SPOF status. A SPOF must break OTHERS.
    if (reach.size - 1 > threshold) {
      result.spofs.add(node.id);
    }
  }

  return result;
}

/**
 * bfsDependents walks the incoming-edge graph from `from`, returning every
 * node reachable (including `from` itself). Pulled out as a helper because
 * the SPOF detector calls it once per node.
 *
 * Uses an iterative queue + visited set so cycles don't loop forever.
 */
function bfsDependents(
  from: string,
  incoming: Map<string, string[]>,
): Set<string> {
  const visited = new Set<string>([from]);
  const queue: string[] = [from];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const dependents = incoming.get(current);
    if (!dependents) continue;
    for (const dep of dependents) {
      if (!visited.has(dep)) {
        visited.add(dep);
        queue.push(dep);
      }
    }
  }

  return visited;
}
